import { forwardRef, useRef, useMemo } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import { AscImage } from "@ascendnow/ascend-components";
import { AscBadgeColor, AscTrackBadgeColor } from "@ascendnow/shared";

import certificateBackground from "../../assets/images/certificate-background.png";
import styles from "./AscCertificate.module.scss";
import AscLogo from "../AscLogo/AscLogo";
import DeviSahny from "../../assets/images/devi-sahny.png";
import EthanBarnes from "../../assets/images/ethan-barnes.png";
import PavanSampath from "../../assets/images/pavan-sampath.png";

export interface AscCertificateProps {
  className?: string;
  date?: string;
  color?: AscBadgeColor | AscTrackBadgeColor;
  name?: string;
  title?: string;
  bgImage?: string;
  earned?: boolean;
}

function splitString(inputString: string, chunkSize: number) {
  const resultArray = [];
  for (let i = 0; i < inputString.length; ) {
    const chunk = inputString
      .slice(i, i + chunkSize)
      .concat(/[a-z0-9]/i.test(inputString.charAt(i + chunkSize)) ? "-" : "");

    resultArray.push(chunk);
    i += chunkSize;
  }
  return resultArray;
}

export default forwardRef<HTMLDivElement, AscCertificateProps>(
  (
    {
      className,
      date = "2023-02-08T12:00:00.000Z",
      color = AscBadgeColor.ORANGE,
      name = "Philipa Yatis",
      title = "Learning",
      earned = true,
    },
    ref
  ) => {
    const ownRef = useRef<HTMLDivElement>(null);
    const dateString = dayjs(new Date(date)).format("MMMM D, YYYY");
    const nameChunks = useMemo(() => splitString(name?.trim(), 20), [name]);

    return (
      <div
        ref={ref || ownRef}
        data-testid="asc-certificate"
        className={classNames(styles.ground, className, {
          [styles.earned]: earned,
        })}
      >
        <AscImage
          decoding="sync"
          src={certificateBackground}
          alt="certificate-background"
          className={styles.imgBackground}
        />
        <div className={classNames(styles.body)}>
          <div className={styles.header}>
            <svg viewBox="0 0 400 20" xmlns="http://www.w3.org/2000/svg">
              <text
                x="50%"
                y="30%"
                dominant-baseline="middle"
                text-anchor="middle"
                className={styles.date}
              >
                {dateString}
              </text>
            </svg>
            <AscLogo variant={color} wrapperClassName={styles.logo} />
          </div>

          <div className={styles.content}>
            <div className={styles.contentTop}>
              <svg viewBox="0 0 658 27" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="50%"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={styles.title}
                >
                  CERTIFICATE
                </text>
              </svg>
              <svg viewBox="0 0 658 17" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="50%"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={styles.secondTitle}
                >
                  Of Skill improvement
                </text>
              </svg>
            </div>

            <div className={styles.nameWrapper}>
              <svg
                viewBox={`0 0 500 ${65 * nameChunks?.length}`}
                xmlns="http://www.w3.org/2000/svg"
                className={styles.nameSvg}
              >
                {nameChunks?.map((nameChunk, index) => (
                  <text
                    key={index}
                    x="50%"
                    y={`${32.5 + index * 65}`}
                    dominant-baseline="middle"
                    text-anchor="middle"
                    className={classNames(styles.name, styles[color])}
                  >
                    {nameChunk}
                  </text>
                ))}
              </svg>

              <hr className={styles.hr} />
            </div>

            <div className={styles.contentBottom}>
              <svg viewBox="0 0 703 17" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="50%"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={styles.secondTitle}
                >
                  HAVING SUCCESSFULLY COMPLETED OVER 20 hours of
                </text>
              </svg>
              <svg viewBox="0 0 703 19" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="50%"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.title, styles.small)}
                >
                  {`${title} TRAINING`}
                </text>
              </svg>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.signatureWrapper}>
              <AscImage
                src={DeviSahny}
                alt="Devi Sahny"
                className={styles.signature}
              />
              <hr className={styles.hr} />
              <svg viewBox="0 0 160 36" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="12"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text, styles.bold)}
                >
                  Devi Sahny
                </text>
                <text
                  x="50%"
                  y="28"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text)}
                >
                  Founder & CEO
                </text>
              </svg>
            </div>
            <div className={styles.signatureWrapper}>
              <AscImage
                src={EthanBarnes}
                alt="Ethan Barnes"
                className={styles.signature}
              />
              <hr className={styles.hr} />
              <svg viewBox="0 0 160 36" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="12"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text, styles.bold)}
                >
                  Ethan Barnes
                </text>
                <text
                  x="50%"
                  y="28"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text)}
                >
                  Partner
                </text>
              </svg>
            </div>
            <div className={styles.signatureWrapper}>
              <AscImage
                src={PavanSampath}
                alt="Pavan Sampath"
                className={styles.signature}
              />
              <hr className={styles.hr} />
              <svg viewBox="0 0 160 36" xmlns="http://www.w3.org/2000/svg">
                <text
                  x="50%"
                  y="12"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text, styles.bold)}
                >
                  Pavan Sampath
                </text>
                <text
                  x="50%"
                  y="28"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  className={classNames(styles.text)}
                >
                  COO & Managing Partner
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
